import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { mdiClose } from "@mdi/js";
import Icon from "@mdi/react";
import clsx from "clsx";
import dayjs from "dayjs";
import { useToggle } from "usehooks-ts";

import Button from "components/Button";
import Modal from "components/Modal";
import { ExclamationSVGIcon } from "components/SVGIcons";
import { HOME_NOTIFICATION_TYPES } from "constants/home";
import { getPath } from "helpers/convertFormData";
import { IActivityItem } from "models/Home";
import { viewActivityAPI } from "services/api/home";
import { AppDispatch, RootState } from "store";
import { handleLoading } from "store/global/loader";
import styles from "./style.module.scss";
import { useAnalytics } from "hooks/useAnalytics";

interface IProps {
  data: IActivityItem;
  removeActivity: (id: string) => void;
}

export default function NotificationItem({ data, removeActivity }: Readonly<IProps>) {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const [isViewActivity, setIsViewActivity] = useState(data?.viewed_on?.$date);
  const [isConfirmModalOpen, toggleConfirmModal] = useToggle();
  const { account } = useSelector((state: RootState) => state.authState);
  const { trackAllEvents } = useAnalytics();
  const time = dayjs(data.created_at).format("hh:mm A");
  const notificationType = HOME_NOTIFICATION_TYPES.filter(
    (item) => item.item_type === data.alert_type
  ).length
    ? HOME_NOTIFICATION_TYPES.filter((item) => item.item_type === data.alert_type)
    : HOME_NOTIFICATION_TYPES.filter((item) => item.item_type === "other");

  const goDetails = async () => {
    const path = getPath(data, account);

    try {
      dispatch(handleLoading(true));
      const response = await viewActivityAPI(data._id.$oid);
      setIsViewActivity(response.data.viewed_on.$date);
      trackAllEvents(`home_notifications_${data.alert_type}`);
    } finally {
      dispatch(handleLoading(false));

      if (path !== "") {
        navigate(path);
      }
    }
  };

  return (
    <li className={styles.homeNotificationItemList}>
      <Button
        className="absolute -top-1.5 -right-1.5 !bg-closeButton !bg-opacity-55 desktop:size-4.5 size-4"
        rounded
        iconButton
        onClick={toggleConfirmModal}>
        <Icon path={mdiClose} className="m-auto size-2.5" />
      </Button>
      <span className="text-time desktop:hidden">{time}</span>
      {notificationType.map((item) => (
        <button
          key={item.id}
          className={clsx(styles.wrapper, isViewActivity ? "opacity-50" : "")}
          onClick={goDetails}>
          <div className={styles.content}>
            <item.icon className="flex-none desktop:mt-px w-5" />
            <div className={styles.contentText}>
              <span className="text-base">{item.title}</span>
              <span className="text-label">
                {item.title === "Welcome to Haulpay!"
                  ? "Take a look around. Apply for finance or subscribe for even more access."
                  : data.message}
              </span>
            </div>
          </div>
          <span className="text-time mobile:hidden text-sm">{time}</span>
        </button>
      ))}

      <Modal
        isOpen={isConfirmModalOpen}
        closeModal={toggleConfirmModal}
        wrapperClass="!my-auto text-sm desktop:!min-w-fit desktop:!min-h-fit"
        dialogClass="!m-auto !bg-foreground !px-6 !py-8 gap-5"
        closeClass="!top-28 !right-6 !bg-transparent !text-text">
        <div className="flex items-center gap-1 text-xl font-bold desktop:text-2xl text-primary">
          <ExclamationSVGIcon />
          <span>Confirm</span>
        </div>
        <div className="pt-1.5 font-medium desktop:text-lg">
          <p>Are you sure you want to delete this activity?</p>
        </div>
        <div className="flex gap-4 justify-end *:w-20 *:desktop:w-24 *:text-sm *:rounded-full *:h-9 *:desktop:h-11 *:desktop:text-base">
          <Button onClick={toggleConfirmModal} variant="outline">
            Cancel
          </Button>
          <Button
            onClick={() => {
              removeActivity(data._id.$oid);
              toggleConfirmModal();
            }}>
            Delete
          </Button>
        </div>
      </Modal>
    </li>
  );
}
